import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import styled from "styled-components";
import { getWeightHistory, updateWeight, getLabDataByName } from "../services/labService";
import { getMemberLabData } from "../services/memberLabService";
import BmiFormula from "../formula/Bmi";
import MainHeader from "../Components/MainHeader";

const WeightContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: "Noto Sans Thai", sans-serif !important;
  * {
    font-family: "Noto Sans Thai", sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const Header = styled.div`
  background-color: #00bbd3;
  padding: 16px 24px;
  color: white;
  font-size: 18px;
  font-weight: bold;
  font-family: "Noto Sans Thai", sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const TitleSection = styled.div`
  margin-bottom: 20px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #b9b9b9;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 8px;
  display: block;
  font-family: "Noto Sans Thai", sans-serif !important;
  transition: all 0.2s ease;

  &:hover {
    color: #999999;
    transform: translateX(-2px);
  }

  &:active {
    transform: translateX(-1px);
  }
`;

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: bold;
  color: #333333;
  margin: 0 0 8px 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const DateText = styled.p`
  font-size: 14px;
  color: #666666;
  margin: 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const StatusCard = styled.div`
  background-color: ${(props) => {
    switch (props.statusColor) {
      case "green":
        return "#34A853";
      case "red":
        return "#ff4757";
      case "gray":
        return "#e0e0e0";
      default:
        return "#e0e0e0";
    }
  }};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
`;

const StatusIcon = styled.div`
  width: 88px;
  height: 88px;
  background-color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusIconImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: contain;
`;

const StatusContent = styled.div`
  flex: 1;
  color: white;
`;

const StatusTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: white;
  margin: 0 0 4px 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const StatusValue = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  margin-bottom: 4px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const StatusLevel = styled.div`
  font-size: 14px;
  color: white;
  margin-bottom: 8px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const StatusDescription = styled.div`
  font-size: 12px;
  color: white;
  line-height: 1.4;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const ChartContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 250px;
  margin-bottom: 16px;
`;

const LegendContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-bottom: 16px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666666;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
`;

const TableContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const TableHeader = styled.div`
  background-color: #00bbd3;
  padding: 16px 20px;
  display: flex;
  color: white;
  font-weight: bold;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const HeaderColumn = styled.div`
  flex: 1;
  font-size: 16px;
  text-align: center;
  color: white;

  &:first-child {
    text-align: left;
  }
`;

const TableRow = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`;

const TableColumn = styled.div`
  font-size: 14px;
  color: #333333;
  font-family: "Noto Sans Thai", sans-serif !important;
  text-align: center;

  &:first-child {
    flex: 1;
    text-align: left;
  }

  &:nth-child(2) {
    flex: 1;
    text-align: center;
  }

  &:nth-child(3) {
    flex: 1;
    text-align: center;
  }

  &:last-child {
    flex: 0 0 auto;
    margin-left: 8px;
  }
`;

const StatusBadge = styled.span`
  color: black;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const EditIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 8px;
  width: 24px;
  height: 24px;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  backdrop-filter: blur(2px);

  @keyframes fadeIn {
    from {
      opacity: 0;
      backdrop-filter: blur(0px);
    }
    to {
      opacity: 1;
      backdrop-filter: blur(2px);
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
      backdrop-filter: blur(2px);
    }
    to {
      opacity: 0;
      backdrop-filter: blur(0px);
    }
  }
`;

const Modal = styled.div`
  background-color: white;
  border-radius: 20px;
  width: 100%;
  max-width: 380px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  transform: translateY(0) scale(1);
  animation: modalSlideIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  will-change: transform, opacity;

  @keyframes modalSlideIn {
    0% {
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
    50% {
      opacity: 0.8;
      transform: translateY(-5px) scale(1.02);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes modalSlideOut {
    0% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    100% {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
  }
`;

const ModalHeader = styled.div`
  background-color: white;
  color: #333333;
  padding: 24px 24px 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: none;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #333333;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #999999;
  font-size: 28px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  will-change: transform, background-color;

  &:hover {
    background-color: #f5f5f5;
    color: #666666;
    transform: scale(1.1) rotate(90deg);
  }

  &:active {
    transform: scale(0.95) rotate(90deg);
  }
`;

const ModalContent = styled.div`
  padding: 24px;

  & > div {
    animation: slideUp 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    animation-fill-mode: both;
  }

  & > div:nth-child(1) {
    animation-delay: 0.1s;
  }
  & > div:nth-child(2) {
    animation-delay: 0.2s;
  }
  & > div:nth-child(3) {
    animation-delay: 0.3s;
  }
  & > div:nth-child(4) {
    animation-delay: 0.4s;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const FormRow = styled.div`
  margin-bottom: 24px;

  &:last-of-type {
    margin-bottom: 32px;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 500;
  color: #333333;
  margin-bottom: 12px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 1px solid #e1e5e9;
  border-radius: 16px;
  font-size: 18px;
  color: #333333;
  background-color: white;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-family: "Noto Sans Thai", sans-serif !important;
  font-weight: 400;
  box-sizing: border-box;
  text-align: left;
  will-change: border-color, box-shadow, transform;

  &:focus {
    outline: none;
    border-color: #00bbd3;
    box-shadow:
      0 0 0 4px rgba(0, 187, 211, 0.15),
      0 4px 20px rgba(0, 187, 211, 0.1);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: #00bbd3;
    transform: translateY(-0.5px);
  }

  &::placeholder {
    color: #999999;
    font-weight: normal;
  }
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const UnitLabel = styled.span`
  margin-left: 12px;
  color: #999999;
  font-size: 16px;
  font-family: "Noto Sans Thai", sans-serif !important;
  flex-shrink: 0;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 32px;
  padding-top: 8px;
`;

const Button = styled.button`
  flex: 1;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const SaveButton = styled(Button)`
  background-color: #00bbd3;
  color: white;
  border-radius: 25px;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  will-change: transform, box-shadow, background-color;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.6s;
  }

  &:hover {
    background-color: #009bb3;
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 25px rgba(0, 187, 211, 0.3);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px) scale(1.01);
    box-shadow: 0 4px 15px rgba(0, 187, 211, 0.4);
  }
`;

const MonthSelectorContainer = styled.div`
  margin-bottom: 24px;
`;

const MonthSelector = styled.select`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid #e1e5e9;
  border-radius: 16px;
  font-size: 16px;
  color: #333333;
  background-color: white;
  font-family: "Noto Sans Thai", sans-serif !important;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23666' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 16px center;
  background-repeat: no-repeat;
  background-size: 20px;
  padding-right: 48px;

  &:hover {
    border-color: #00bbd3;
    box-shadow: 0 2px 8px rgba(0, 187, 211, 0.1);
  }

  &:focus {
    outline: none;
    border-color: #00bbd3;
    box-shadow: 0 0 0 4px rgba(0, 187, 211, 0.15);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 20px;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #00bbd3;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-size: 16px;
  color: #666666;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const FadeInContainer = styled.div`
  animation: fadeIn 0.5s ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  margin-top: 16px;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${(props) => (props.$disabled ? "#e0e0e0" : "#00BBD3")};
  background-color: ${(props) =>
    props.$disabled ? "#f5f5f5" : props.$active ? "#00BBD3" : "white"};
  color: ${(props) =>
    props.$disabled ? "#999" : props.$active ? "white" : "#00BBD3"};
  border-radius: 8px;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? "bold" : "normal")};
  transition: all 0.2s ease;
  font-family: "Noto Sans Thai", sans-serif !important;
  min-width: 40px;

  &:hover {
    background-color: ${(props) =>
      props.$disabled ? "#f5f5f5" : props.$active ? "#00BBD3" : "#e6f7f9"};
    transform: ${(props) => (props.$disabled ? "none" : "translateY(-2px)")};
  }

  &:active {
    transform: ${(props) => (props.$disabled ? "none" : "translateY(0)")};
  }
`;

const PageInfo = styled.div`
  font-size: 14px;
  color: #666;
  font-family: "Noto Sans Thai", sans-serif !important;
  padding: 0 8px;
`;

const WeightPage = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allData, setAllData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [dateRange, setDateRange] = useState("");
  const [latestStatus, setLatestStatus] = useState(null);
  const [userData, setUserData] = useState({
    height: null,
    apiBmi: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Load weight data from API
  const loadWeightData = async () => {
    try {
      setLoading(true);

      // ดึงข้อมูลจาก GetLabDataByName API
      let dataArray = [];
      const response = await getWeightHistory();

      if (response?.data === null || response?.data === undefined) {
        dataArray = [];
      } else if (Array.isArray(response?.data)) {
        dataArray = response.data;
      } else if (typeof response?.data === "object") {
        dataArray = [];
      } else {
        dataArray = [];
      }


      if (dataArray && dataArray.length > 0) {
        // Sort by date descending (newest first) to ensure [0] is the latest
        const sortedData = dataArray.sort((a, b) => {
          const dateA = new Date(a.dateadd).getTime();
          const dateB = new Date(b.dateadd).getTime();
          return dateB - dateA; // descending: newest first
        });


        // Store all data (sorted)
        setAllData(sortedData);

        // Calculate latest status using BmiFormula
        // BMI will be calculated after profile is loaded with height
        const latestRecord = sortedData[0];
        setLatestStatus({ weight: latestRecord.value }); // Temporary, will be updated after profile load

        // Extract available months (last 12 months with data)
        const monthsMap = new Map();
        dataArray.forEach((item) => {
          const date = new Date(item.dateadd);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
          if (!monthsMap.has(monthKey)) {
            monthsMap.set(monthKey, {
              key: monthKey,
              year: date.getFullYear(),
              month: date.getMonth(),
              date: date,
            });
          }
        });

        // Sort months by date (newest first) and limit to 12 months
        const months = Array.from(monthsMap.values())
          .sort((a, b) => b.date - a.date)
          .slice(0, 12);

        setAvailableMonths(months);

        // Set selected month to the most recent month
        if (months.length > 0) {
          setSelectedMonth(months[0].key);
        }

      } else {
        setAllData([]);
        setChartData([]);
        setTableData([]);
        setDateRange("ยังไม่มีข้อมูล");
        setAvailableMonths([]);
        setSelectedMonth(null);
        setLatestStatus(null);
      }
    } catch (error) {
      setAllData([]);
      setChartData([]);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWeightData();
  }, []);

  // Process data for display
  useEffect(() => {
    if (allData.length === 0) {
      return;
    }

    const filteredData = allData;


    // Transform data for chart (all records in selected month, sorted by date)
    // Use spread operator to avoid mutating original allData array
    // record ล่าสุด (allData[0]) ใช้ค่า bmi จาก API (ยึดตาม mfu-admin)
    const latestRawDate = allData[0]?.dateadd;
    const apiBmiNum = parseFloat(userData.apiBmi);

    const chartRecords = [...filteredData]
      .sort((a, b) => new Date(a.dateadd) - new Date(b.dateadd))
      .map((item) => {
        const date = new Date(item.dateadd);
        const formattedDate = `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}`;

        // คำนวณ BMI
        const weight = parseFloat(item.value);
        let bmi = 0;
        const height = parseFloat(userData.height);
        if (height && height > 0) {
          const heightInMeters = height / 100;
          bmi = weight / (heightInMeters * heightInMeters);
        }

        // ถ้าเป็น record ล่าสุดและมีค่า bmi จาก API ให้ใช้ค่านั้น
        if (item.dateadd === latestRawDate && isFinite(apiBmiNum) && apiBmiNum > 0) {
          bmi = apiBmiNum;
        }

        return {
          date: formattedDate,
          BMI: isFinite(bmi) ? parseFloat(bmi.toFixed(1)) : 0, // แสดง BMI แทน weight ในกราฟ (ทศนิยม 1 ตำแหน่ง)
        };
      });

    // Transform data for table
    const tableRecords = filteredData.map((item) => {
      const date = new Date(item.dateadd);
      const formattedDate = `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear().toString().slice(-2)}`;

      // Use BmiFormula to determine status (requires height)
      let status = "ปกติ";
      const height = parseFloat(userData.height);
      if (height && height > 0) {
        const weight = parseFloat(item.value);
        const heightInMeters = height / 100;
        const bmi = weight / (heightInMeters * heightInMeters);
        if (isFinite(bmi)) {
          const statusResult = BmiFormula(bmi);
          if (
            statusResult &&
            (statusResult.color === "red" || statusResult.color === "orange")
          ) {
            status = "ผิดปกติ";
          }
        }
      }

      return {
        date: formattedDate,
        value: item.value,
        status: status,
        type: "normal",
        id: item.id || item.dateadd,
        rawDate: item.dateadd,
      };
    });

    setChartData(chartRecords);
    setTableData(tableRecords);

    // Calculate date range for display
    if (filteredData.length > 0) {
      const dates = filteredData.map((item) => new Date(item.dateadd));
      const oldestDate = new Date(Math.min(...dates));
      const newestDate = new Date(Math.max(...dates));

      const thaiMonths = [
        "มกราคม",
        "กุมภาพันธ์",
        "มีนาคม",
        "เมษายน",
        "พฤษภาคม",
        "มิถุนายน",
        "กรกฎาคม",
        "สิงหาคม",
        "กันยายน",
        "ตุลาคม",
        "พฤศจิกายน",
        "ธันวาคม",
      ];

      const formatThaiDate = (date) => {
        const day = date.getDate();
        const month = thaiMonths[date.getMonth()];
        const year = date.getFullYear() + 543;
        return `${day} ${month} ${year}`;
      };

      if (filteredData.length === 1) {
        setDateRange(`ผลตรวจวันที่ ${formatThaiDate(newestDate)}`);
      } else {
        setDateRange(
          `ผลตรวจวันที่ ${formatThaiDate(oldestDate)} - ${formatThaiDate(newestDate)}`,
        );
      }
    } else {
      setDateRange("ยังไม่มีข้อมูล");
    }
  }, [allData, userData.height, userData.apiBmi]);

  // Load user profile data (for height only, needed for BMI calculation)
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const { getProfile } = await import("../services/profileService");
        const response = await getProfile();
        let profile = response?.data || response?.profile;
        let height = profile?.height || null;

        // ถ้า ProfileGet ส่ง height เป็น "0" หรือไม่มี ให้ดึงจาก GetLabDataByName หรือ MemberLabList
        if (!height || height === '0' || height === 0) {
          try {
            const heightRes = await getLabDataByName('height');
            const heightData = (heightRes?.data || []).sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
            if (heightData.length > 0) {
              height = heightData[0].value;
            }
          } catch (e) {}
        }
        let apiBmi = null;
        try {
          const memberData = await getMemberLabData();
          if ((!height || height === '0' || height === 0)) {
            const labHeight = memberData?.labData?.['height']?.[0]?.value;
            if (labHeight) height = labHeight;
          }
          // ดึงค่า bmi ที่หลังบ้านคำนวณไว้ (ยึดตาม mfu-admin)
          apiBmi = memberData?.labData?.['bmi']?.[0]?.value || null;
        } catch (labError) {
        }

        setUserData({
          height: height,
          apiBmi: apiBmi,
        });
      } catch (error) {
      }
    };

    loadProfile();
  }, []);

  // Recalculate BMI status — ใช้ค่า bmi จาก API ก่อน (ยึดตาม mfu-admin) ถ้าไม่มีค่อยคำนวณเอง
  // ปิด logic การตัดสินสี/สถานะ (แดง/เขียว/ผิดปกติ) จาก BmiFormula ทั้งหมด — บังคับให้เป็นสีเทาเสมอ
  useEffect(() => {
    // const apiBmi = parseFloat(userData.apiBmi);
    // if (isFinite(apiBmi) && apiBmi > 0) {
    //   setLatestStatus(BmiFormula(apiBmi));
    //   return;
    // }
    // const height = parseFloat(userData.height);
    // if (height && height > 0 && allData.length > 0) {
    //   const latestWeight = parseFloat(allData[0].value);
    //   const heightInMeters = height / 100;
    //   const bmi = latestWeight / (heightInMeters * heightInMeters);

    //   if (isFinite(bmi)) {
    //     const statusResult = BmiFormula(bmi);
    //     setLatestStatus(statusResult);
    //   }
    // }
    setLatestStatus({ color: "gray" });
  }, [userData.height, userData.apiBmi, allData]);

  const handleBackClick = () => {
    navigate("/dashboard");
  };

  const handleEditClick = (item, index) => {
    setEditingData({
      ...item,
      index,
    });
    setShowModal(true);
  };

  const handleModalClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowModal(false);
      setEditingData(null);
      setIsClosing(false);
    }, 250);
  };

  const handleSave = async () => {
    try {

      // Add a subtle success animation
      const button = document.querySelector('button[type="submit"]');
      if (button) {
        button.style.transform = "scale(0.95)";
        setTimeout(() => {
          button.style.transform = "scale(1)";
        }, 150);
      }

      await updateWeight(editingData.rawDate, editingData.value);

      alert("บันทึกข้อมูลเรียบร้อยแล้ว");
      handleModalClose();
      loadWeightData();
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง");
    }
  };

  return (
    <WeightContainer>
      <MainHeader />

      <Header>น้ำหนัก</Header>

      <Content>
        <TitleSection>
          <BackButton onClick={handleBackClick}>‹ ย้อนกลับ</BackButton>
          <PageTitle style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <StatusIconImage src="./น้ำหนักสี.png" alt="Weight Icon" style={{ width: "32px", height: "32px" }} />
            น้ำหนัก
          </PageTitle>
          <DateText>{dateRange || "กำลังโหลด..."}</DateText>
        </TitleSection>

        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>กำลังโหลดข้อมูล...</LoadingText>
          </LoadingContainer>
        ) : (
          <>
            {/* Status Card — comment ทั้งการ์ดออก เพราะย้ายไอคอนไปไว้ที่ Header ด้านบนแล้ว */}
            {/* {allData.length > 0 && (
              <FadeInContainer>
                {/* Status Card *\/}
                {/* ปิด logic การตัดสินสี/สถานะ (แดง/เขียว/ผิดปกติ) ทั้งหมด — เหลือแค่ไอคอน + ชื่อหมวดเท่านั้น *\/}
                {/* <StatusCard statusColor={latestStatus?.color || "gray"}> *\/}
                <StatusCard statusColor="gray">
                  <StatusIcon>
                    <StatusIconImage src="./น้ำหนักสี.png" alt="Weight Icon" />
                  </StatusIcon>
                  <StatusContent>
                    <StatusTitle>น้ำหนัก</StatusTitle>
                    {/* <StatusValue>
                      {allData[0]?.value} kg
                      {(() => {
                        // ใช้ค่า bmi จาก API ก่อน (ยึดตาม mfu-admin) ถ้าไม่มีค่อยคำนวณเอง
                        const apiBmi = parseFloat(userData.apiBmi);
                        if (isFinite(apiBmi) && apiBmi > 0) {
                          return ` (BMI: ${apiBmi.toFixed(1)})`;
                        }
                        const height = parseFloat(userData.height);
                        if (height && height > 0) {
                          const weight = parseFloat(allData[0]?.value);
                          const heightInMeters = height / 100;
                          const bmi = weight / (heightInMeters * heightInMeters);
                          if (isFinite(bmi)) {
                            return ` (BMI: ${bmi.toFixed(1)})`;
                          }
                        }
                        return '';
                      })()}
                    </StatusValue>
                    <StatusLevel>
                      {latestStatus?.title || "กำลังคำนวณ..."}
                    </StatusLevel> *\/}
                  </StatusContent>
                </StatusCard>
              </FadeInContainer>
            )} */}

            {/* ช่องเดือนปีถูกซ่อนไว้ */}

            {chartData.length > 0 && (
              <FadeInContainer>
                <ChartContainer>
                  <LegendContainer>
                    <LegendItem>
                      <LegendDot color="#FF5722" />
                      <span>BMI</span>
                    </LegendItem>
                  </LegendContainer>

                  <ChartWrapper>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="date"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: "#666666" }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis
                          domain={[10, 50]}
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: "#666666" }}
                          label={{ value: "BMI", angle: -90, position: "insideLeft" }}
                        />
                        <Tooltip
                          formatter={(value) => [value, 'BMI']}
                          labelStyle={{ color: '#666' }}
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                        />
                        <Line
                          type="monotone"
                          dataKey="BMI"
                          stroke="#FF5722"
                          strokeWidth={3}
                          dot={{ fill: "#FF5722", strokeWidth: 2, r: 6 }}
                          activeDot={{ r: 8, fill: "#FF5722" }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartWrapper>
                </ChartContainer>
              </FadeInContainer>
            )}

            {tableData.length > 0 &&
              (() => {
                // Calculate pagination
                const totalPages = Math.ceil(tableData.length / itemsPerPage);
                const startIndex = (currentPage - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const currentPageData = tableData.slice(startIndex, endIndex);

                return (
                  <FadeInContainer>
                    <TableContainer>
                      <TableHeader>
                        <HeaderColumn>วันที่ตรวจ</HeaderColumn>
                        <HeaderColumn>ค่าที่วัด</HeaderColumn>
                        <HeaderColumn>ความเสี่ยง</HeaderColumn>
                      </TableHeader>

                      {currentPageData.map((item, index) => (
                        <TableRow key={startIndex + index}>
                          <TableColumn>{item.date}</TableColumn>
                          <TableColumn>{item.value} kg</TableColumn>
                          <TableColumn>
                            <StatusBadge type={item.type}>
                              {item.status}
                            </StatusBadge>
                          </TableColumn>
                          <TableColumn>
                            {/* ปิดการแก้ไขข้อมูลจากตารางประวัติ — comment ไอคอนแก้ไขไว้ ไม่ลบ */}
                            {/* <EditIcon
                              onClick={() =>
                                handleEditClick(item, startIndex + index)
                              }
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <g clipPath="url(#clip0_1060_3686)">
                                  <path
                                    d="M3.5 24H18.5C19.4297 23.9974 20.3204 23.626 20.9765 22.9674C21.6327 22.3087 22.0008 21.4167 22 20.487V12.95C22 12.6848 21.8946 12.4304 21.7071 12.2429C21.5196 12.0554 21.2652 11.95 21 11.95C20.7348 11.95 20.4804 12.0554 20.2929 12.2429C20.1054 12.4304 20 12.6848 20 12.95V20.487C20.0013 20.8864 19.8441 21.2701 19.5629 21.5537C19.2817 21.8374 18.8994 21.9979 18.5 22H3.5C3.10057 21.9979 2.7183 21.8374 2.43708 21.5537C2.15587 21.2701 1.99867 20.8864 2 20.487V5.513C1.99867 5.11357 2.15587 4.72993 2.43708 4.44627C2.7183 4.16262 3.10057 4.00212 3.5 4H11C11.2652 4 11.5196 3.89464 11.7071 3.70711C11.8946 3.51957 12 3.26522 12 3C12 2.73478 11.8946 2.48043 11.7071 2.29289C11.5196 2.10536 11.2652 2 11 2H3.5C2.57031 2.00265 1.67964 2.37403 1.02346 3.03265C0.367281 3.69126 -0.000797091 4.5833 1.29611e-06 5.513V20.487C-0.000797091 21.4167 0.367281 22.3087 1.02346 22.9674C1.67964 23.626 2.57031 23.9974 3.5 24Z"
                                    fill="#34A853"
                                  />
                                  <path
                                    d="M9.45499 10.544L8.66599 14.158C8.63027 14.3219 8.63638 14.4922 8.68373 14.6531C8.73109 14.8141 8.81818 14.9605 8.93699 15.079C9.05742 15.1945 9.20393 15.2792 9.36408 15.326C9.52423 15.3729 9.69331 15.3804 9.85699 15.348L13.463 14.557C13.6502 14.5159 13.8217 14.4218 13.957 14.286L23.071 5.17197C23.3496 4.89339 23.5706 4.56265 23.7214 4.19864C23.8722 3.83463 23.9498 3.44448 23.9498 3.05047C23.9498 2.65647 23.8722 2.26632 23.7214 1.90231C23.5706 1.5383 23.3496 1.20756 23.071 0.928973C22.4998 0.38311 21.7401 0.0784912 20.95 0.0784912C20.1599 0.0784912 19.4002 0.38311 18.829 0.928973L9.72899 10.052C9.59241 10.1863 9.4973 10.3571 9.45499 10.544ZM20.243 2.34397C20.4332 2.16171 20.6865 2.05997 20.95 2.05997C21.2135 2.05997 21.4667 2.16171 21.657 2.34397C21.842 2.5326 21.9456 2.78627 21.9456 3.05047C21.9456 3.31468 21.842 3.56834 21.657 3.75697L20.95 4.46397L19.536 3.04997L20.243 2.34397ZM11.343 11.258L18.117 4.46697L19.517 5.87397L12.74 12.667L10.945 13.061L11.343 11.258Z"
                                    fill="#34A853"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_1060_3686">
                                    <rect width="24" height="24" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            </EditIcon> */}
                          </TableColumn>
                        </TableRow>
                      ))}
                    </TableContainer>

                    <PaginationContainer>
                      <PageInfo>
                        หน้า {currentPage} จาก {totalPages} ({tableData.length}{" "}
                        รายการ)
                      </PageInfo>
                    </PaginationContainer>
                  </FadeInContainer>
                );
              })()}
          </>
        )}
      </Content>

      {showModal && editingData && (
        <ModalOverlay
          onClick={handleModalClose}
          style={{
            animation: isClosing
              ? "fadeOut 0.25s ease-out forwards"
              : undefined,
          }}
        >
          <Modal
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: isClosing
                ? "modalSlideOut 0.25s ease-out forwards"
                : undefined,
            }}
          >
            <ModalHeader>
              <ModalTitle>แก้ไขข้อมูลน้ำหนัก</ModalTitle>
              <CloseButton onClick={handleModalClose}>×</CloseButton>
            </ModalHeader>
            <ModalContent>
              <FormRow>
                <Label>วันที่ตรวจ {editingData.date}</Label>
              </FormRow>

              <FormRow>
                <Label>น้ำหนัก</Label>
                <InputContainer>
                  <Input
                    type="number"
                    value={editingData.value}
                    onChange={(e) =>
                      setEditingData({ ...editingData, value: e.target.value })
                    }
                    placeholder=""
                  />
                  <UnitLabel>kg</UnitLabel>
                </InputContainer>
              </FormRow>

              <ButtonRow>
                <SaveButton onClick={handleSave} type="submit">
                  บันทึก
                </SaveButton>
              </ButtonRow>
            </ModalContent>
          </Modal>
        </ModalOverlay>
      )}
    </WeightContainer>
  );
};

export default WeightPage;
